import React, { useRef, useState } from "react";
import * as api from "../../../../backend/request";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faTrash } from "@fortawesome/free-solid-svg-icons";
import { toast } from "react-toastify";
import { useGetVendorActivityDocuments } from "../../../../backend/query";
import PageLoader from "../../../shared/PageLoader";
import { STATUS_MAPPING } from "../../../common/Constants";
import { checkVendorActivityStatus, downloadWithPresignedUrl } from "../../../../utils/common";
import { AxiosError } from "axios";
import { validateFileWithContent, FILE_TYPE_GROUPS, FILE_SIZE_LIMITS, generateAcceptAttribute, VALIDATION_MESSAGES } from "../../../../utils/fileValidation";
import "../../../pages/Auditor/ActivityModalV2.css";
import cloudUpload from "../../../../assets/img/icon/cloudUpload.svg";

function StatusTmp({ status }: any) {
  return <span className={`status-${status}`}>{STATUS_MAPPING[status]}</span>;
}

function EditActivityModal({ activity = {}, auditStatus, onClose, onSubmit }: any) {
  const [submitting, setSubmitting] = useState(false);
  const [formStatus] = useState(checkVendorActivityStatus(activity, auditStatus));
  const [auditRemarks, setAuditRemarks] = useState<any>(activity.auditee_remarks || "");
  const [file, setFile] = useState<any>(null);
  const [invalidFile, setInvalidFile] = useState(false);
  const { documents, invalidate } = useGetVendorActivityDocuments(activity.id);
  const fileInputRef = useRef<HTMLInputElement>(null);

  async function onFileChange(event: any) {
    const selectedFile = event.target.files[0];
    if (!selectedFile) { setFile(null); setInvalidFile(false); return; }
    setInvalidFile(false);
    const validation = await validateFileWithContent(selectedFile, FILE_TYPE_GROUPS.ALL_DOCUMENTS, FILE_SIZE_LIMITS.LARGE);
    if (!validation.isValid) {
      setInvalidFile(true); setFile(null);
      toast.error(validation.error);
      event.target.value = '';
      return;
    }
    setFile(selectedFile);
    setInvalidFile(false);
  }

  function clearFile() {
    setFile(null);
    setInvalidFile(false);
    if (fileInputRef.current) fileInputRef.current.value = '';
  }

  function uploadFile() {
    setSubmitting(true);
    const formData = new FormData();
    formData.append("file", file, file.name);
    api
      .post(`/api/v1/todo_vendor_details/upload?todo_vendor_id=${activity.id}`, formData, {}, true, { cotent_type: 'multipart-form-data' })
      .then((response: any) => {
        if (response instanceof AxiosError) {
          const data = (response.response || {}).data;
          toast.error((data || {}).error || "File upload failed.");
        } else {
          setFile(null); setInvalidFile(false);
          if (fileInputRef.current) fileInputRef.current.value = '';
          invalidate();
          toast.success('File uploaded successfully.');
        }
      })
      .catch((error: any) => {
        const data = (error && error.response && error.response.data) || {};
        toast.error(data.error || data.message || 'File upload failed.');
        const details = data.details;
        if (details && details.length > 0) {
          details.forEach(function(detail: string) { toast.error(detail); });
        }
      })
      .finally(() => setSubmitting(false));
  }

  function downloadFile(doc: any) {
    downloadWithPresignedUrl(doc.file_name, doc.file_path);
  }

  function deleteFile(doc: any) {
    setSubmitting(true);
    api
      .del(`/api/v1/todo_vendor_details/${doc.id}`)
      .then((response: any) => {
        if (response instanceof AxiosError) {
          const data = (response.response || {}).data;
          toast.error((data || {}).error || "File deletion failed.");
        } else {
          invalidate();
          toast.success('File deleted successfully.');
        }
        invalidate();
      })
      .catch(() => toast.error('File deletion failed.'))
      .finally(() => setSubmitting(false));
  }

  function submit() {
    setSubmitting(true);
    api
      .put(`/api/v1/todo_vendors/${activity.id}/save_activity`, { auditee_remarks: auditRemarks, status: 'ActivitySaved' })
      .then((response: any) => {
        if (response instanceof AxiosError) {
          const data = (response.response || {}).data;
          toast.error((data || {}).error || "Activity saving failed.");
        } else {
          toast.success("Activity saved successfully.");
          onClose();
          onSubmit();
        }
      })
      .finally(() => setSubmitting(false));
  }

  if (!formStatus) return null;

  return (
    <>
      <div className="la-drawer-backdrop open" />
      <div className="la-drawer activity-edit-drawer open">
        <div className="la-drawer-head">
          <div className="la-drawer-title">{formStatus.editable ? "Edit Activity" : "Activity Details"}</div>
          <button className="la-drawer-close" onClick={onClose} aria-label="Close">
            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <line x1="18" y1="6" x2="6" y2="18" /><line x1="6" y1="6" x2="18" y2="18" />
            </svg>
          </button>
        </div>

        <div className="ae-body">
          <div className="ae-section">

            {formStatus.message && (
              <div className="ae-row" style={{ display: "block", padding: "10px 0" }}>
                <div className={`alert alert-${formStatus.type}`} style={{ margin: 0 }}>{formStatus.message}</div>
              </div>
            )}

            <div className="ae-row">
              <label>Act</label>
              <div className="ae-val">{(activity.act || {}).name || '—'}</div>
            </div>
            <div className="ae-row">
              <label>Rule</label>
              <div className="ae-val">{(activity.rule || {}).name || '—'}</div>
            </div>
            <div className="ae-row">
              <label>Forms/Registers &amp; Returns</label>
              <div className="ae-val">{(activity.activity || {}).name || '—'}</div>
            </div>
            <div className="ae-row">
              <label>Activity Type</label>
              <div className="ae-val">{(activity.activity || {}).activity_type || '—'}</div>
            </div>
            <div className="ae-row">
              <label>Month (Year)</label>
              <div className="ae-val">{activity.month ? `${activity.month} (${activity.year})` : '—'}</div>
            </div>
            <div className="ae-row">
              <label>Evidence Status</label>
              <div className="ae-val">{activity.status ? <StatusTmp status={activity.status} /> : '—'}</div>
            </div>
            <div className="ae-row">
              <label>Auditee Remarks</label>
              <div className="ae-val">{activity.auditee_remarks || '—'}</div>
            </div>
            <div className="ae-row">
              <label>Observations</label>
              <div className="ae-val"><div dangerouslySetInnerHTML={{ __html: activity.forms_status_remarks || '' }} /></div>
            </div>
            <div className="ae-row">
              <label>Recommendations</label>
              <div className="ae-val"><div dangerouslySetInnerHTML={{ __html: activity.audit_remarks || '' }} /></div>
            </div>

            {formStatus.editable && (
              <div className="ae-row" style={{ flexDirection: "column", alignItems: "stretch" }}>
                <label>Auditee Remarks</label>
                <textarea
                  className="ae-textarea"
                  rows={3}
                  value={auditRemarks}
                  onChange={(e: any) => setAuditRemarks(e.target.value)}
                  placeholder="Enter auditee remarks here..."
                />
              </div>
            )}

            <div className="ae-evidence-header">Evidence</div>

            {formStatus.editable && (
              <>
                <input
                  ref={fileInputRef}
                  type="file"
                  style={{ display: 'none' }}
                  onChange={onFileChange}
                  accept={generateAcceptAttribute(FILE_TYPE_GROUPS.ALL_DOCUMENTS)}
                />
                {!file ? (
                  <div className="ae-upload-box" onClick={() => { if (fileInputRef.current) fileInputRef.current.click(); }}>
                    <div className="mb-2">
                      <img src={cloudUpload} alt="upload" />
                    </div>
                    <p className="mb-2">Click to browse and upload</p>
                    <button
                      type="button"
                      onClick={(e) => { e.stopPropagation(); if (fileInputRef.current) fileInputRef.current.click(); }}
                    >
                      Choose File
                    </button>
                  </div>
                ) : (
                  <>
                    <div className="ae-upload-success">
                      <span className="ae-upload-filename">{file.name}</span>
                      <button className="ae-upload-remove" onClick={clearFile} type="button">
                        <svg width="16" height="16" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg">
                          <path d="M10.0002 5.99992L6.00016 9.99992M6.00016 5.99992L10.0002 9.99992M14.6668 7.99992C14.6668 11.6818 11.6821 14.6666 8.00016 14.6666C4.31826 14.6666 1.3335 11.6818 1.3335 7.99992C1.3335 4.31802 4.31826 1.33325 8.00016 1.33325C11.6821 1.33325 14.6668 4.31802 14.6668 7.99992Z" stroke="#757575" strokeWidth="1.33333" strokeLinecap="round" strokeLinejoin="round"/>
                        </svg>
                      </button>
                    </div>
                    <button
                      className="ae-upload-btn"
                      disabled={invalidFile || submitting}
                      onClick={uploadFile}
                      type="button"
                    >
                      {submitting ? 'Uploading...' : 'Upload'}
                    </button>
                  </>
                )}
                <p className="ae-upload-hint">{VALIDATION_MESSAGES.ALLOWED_FILE_ALL_DOCS}</p>
                {invalidFile && (
                  <div className="text-danger mt-1">
                    <small>{VALIDATION_MESSAGES.INVALID_FILE_ALL_DOCS}</small>
                  </div>
                )}
              </>
            )}

            <div style={{ marginTop: 16 }}>
              <table className="table modalTable fixed_header">
                <thead>
                  <tr>
                    <th style={{ width: "75%" }}>File Name</th>
                    <th style={{ width: "25%" }}>Actions</th>
                  </tr>
                </thead>
                <tbody style={{ height: "300px" }}>
                  {documents.map((doc: any, index: number) => (
                    <tr key={index}>
                      <td width="75%" style={{ overflowWrap: "break-word" }}>{doc.file_name}</td>
                      <td width="25%">
                        <div className="d-flex flex-row align-items-center gap-3">
                          <span style={{ cursor: "pointer", opacity: 0.7 }} onClick={() => downloadFile(doc)} title="Download">
                            <svg width="16" height="16" viewBox="0 0 12 12" fill="none" xmlns="http://www.w3.org/2000/svg">
                              <path d="M9.75 7.875V9.75H2.25V7.875H1V9.75C1 10.4375 1.5625 11 2.25 11H9.75C10.4375 11 11 10.4375 11 9.75V7.875H9.75Z" fill="var(--bs-blue)" />
                              <path d="M9.125 5.375L8.24375 4.49375L6.625 6.10625L6.625 1L5.375 1L5.375 6.10625L3.75625 4.49375L2.875 5.375L6 8.5L9.125 5.375Z" fill="var(--bs-blue)" />
                            </svg>
                          </span>
                          {formStatus.editable && (
                            <span style={{ cursor: "pointer", opacity: 0.7, color: "var(--red)" }} onClick={() => deleteFile(doc)} title="Delete">
                              <FontAwesomeIcon icon={faTrash} />
                            </span>
                          )}
                        </div>
                      </td>
                    </tr>
                  ))}
                  {documents.length === 0 && (
                    <tr>
                      <td colSpan={2} rowSpan={4} width="100%" style={{ height: "200px", border: 0 }}>
                        <div className="d-flex w-100 h-100 align-items-center justify-content-center">No files available</div>
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>

          </div>
        </div>

        <div className="ae-foot">
          {formStatus.editable ? (
            <>
              <button className="ae-back" onClick={onClose}>Cancel</button>
              <button className="ae-submit" onClick={submit} disabled={submitting}>
                {submitting ? "Saving..." : "Save Activity"}
              </button>
            </>
          ) : (
            <button className="ae-submit" style={{ flex: 1 }} onClick={onClose}>Close</button>
          )}
        </div>
      </div>

      {submitting && <PageLoader />}
    </>
  );
}

export default EditActivityModal;
